// src/views/audit/AuditLogs.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../services/api";
import { getApiBaseUrl } from "../../services/apiBase";
import { useAuth } from "../../contexts/AuthContext";
import condominiosService from "../../services/condominiosService";

const fmtDT = d => { try { if(!d) return "—"; const s=String(d).includes("T")?d:d+" 00:00:00"; return new Date(s).toLocaleString("pt-BR"); } catch { return d||"—"; } };

const ACAO_BADGE = {
  CREATE:           { bg:"#DCFCE7", color:"#166534", label:"Criação"     },
  UPDATE:           { bg:"#DBEAFE", color:"#1D4ED8", label:"Alteração"   },
  DELETE:           { bg:"#FEE2E2", color:"#991B1B", label:"Exclusão"    },
  LOGIN:            { bg:"#F3E8FF", color:"#7C3AED", label:"Login"       },
  READ:             { bg:"#F1F5F9", color:"var(--gray-500)", label:"Consulta"    },
  ALTER_PASSWORD:   { bg:"#FEF3C7", color:"#92400E", label:"Senha"       },
  LINK_CONDOMINIOS: { bg:"#E0E7FF", color:"#3730A3", label:"Vinculação"  },
  EXPORT:           { bg:"#FCE7F3", color:"#9D174D", label:"Exportação"  },
};

const PERFIL_BADGE = {
  Admin:     { bg:"#DBEAFE", color:"#1D4ED8" },
  Sindico:   { bg:"#DCFCE7", color:"#166534" },
  Condomino: { bg:"#FEF3C7", color:"#92400E" },
  Porteiro:  { bg:"#F3E8FF", color:"#7C3AED" },
};

// Ações consideradas críticas (destacar visualmente)
const ACOES_CRITICAS = new Set(["DELETE", "ALTER_PASSWORD"]);

// Banner de erro/sucesso reutilizável
function Banner({ tipo="info", children, onClose }) {
  const cores = {
    error:   { bg:"#FEE2E2", border:"#FECACA", color:"#991B1B", icon:"bi-exclamation-octagon" },
    success: { bg:"#DCFCE7", border:"#BBF7D0", color:"#166534", icon:"bi-check-circle"        },
    info:    { bg:"#EFF6FF", border:"#BFDBFE", color:"#1D4ED8", icon:"bi-info-circle"          },
    warning: { bg:"#FEF3C7", border:"#FDE68A", color:"#92400E", icon:"bi-exclamation-triangle" },
  };
  const c = cores[tipo] || cores.info;
  if (!children) return null;
  return (
    <div style={{
      background:c.bg, border:`1px solid ${c.border}`, color:c.color,
      borderRadius:8, padding:"10px 14px", marginBottom:12,
      display:"flex", alignItems:"center", gap:10, fontSize:13.5,
    }}>
      <i className={`bi ${c.icon}`} style={{fontSize:18}}/>
      <div style={{flex:1}}>{children}</div>
      {onClose && (
        <button className="btn btn-sm" style={{padding:"2px 8px", background:"transparent", color:c.color}}
          onClick={onClose}><i className="bi bi-x-lg"/></button>
      )}
    </div>
  );
}

export default function AuditLogs() {
  const { isAdmin } = useAuth();

  if (!isAdmin) return (
    <div style={{textAlign:"center",padding:60,color:"var(--gray-400)"}}>
      <i className="bi bi-shield-lock" style={{fontSize:48,display:"block",marginBottom:12}}/>
      <p style={{fontSize:15}}>Acesso restrito a administradores.</p>
    </div>
  );

  const [condominios,  setCondominios]  = useState([]);
  const [modulosOpts,  setModulosOpts]  = useState([]); // do backend
  const [logs,         setLogs]         = useState([]);
  const [stats,        setStats]        = useState(null);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [loading,      setLoading]      = useState(false);
  const [detalhe,      setDetalhe]      = useState(null);
  const [showLimpar,   setShowLimpar]   = useState(false);
  const [limpando,     setLimpando]     = useState(false);
  const [exportando,   setExportando]   = useState(false);
  const [configLimpar, setConfigLimpar] = useState({ dias_read:90, dias_geral:365, dias_financeiro:1825 });

  const [pageError, setPageError]   = useState("");
  const [pageSuccess, setPageSuccess] = useState("");

  const [filtros, setFiltros] = useState({
    idCondominio:"", modulo:"", acao:"", dtIni:"", dtFim:"", busca:"",
  });

  const LIMIT = 50;

  useEffect(() => {
    condominiosService.listar().then(r => setCondominios(r.data || [])).catch(() => {});
    api("GET", "/audit/modulos").then(r => setModulosOpts(r.data || [])).catch(() => {});
    carregarStats();
  }, []);

  const carregarStats = async () => {
    try {
      const r = await api("GET", "/audit/stats/resumo");
      setStats(r.data);
    } catch (e) {
      setPageError(`Não foi possível carregar estatísticas: ${e.message}`);
    }
  };

  const carregar = useCallback(async (p = 1) => {
    setLoading(true);
    setPageError("");
    try {
      const qs = new URLSearchParams({
        ...Object.fromEntries(Object.entries(filtros).filter(([, v]) => v)),
        page: p, limit: LIMIT,
      });
      const r = await api("GET", `/audit?${qs}`);
      setLogs(r.data || []);
      setTotal(r.total || 0);
      setPage(p);
    } catch (e) {
      setPageError(`Erro ao carregar logs: ${e.message}`);
    }
    setLoading(false);
  }, [filtros]);

  useEffect(() => { carregar(1); /* eslint-disable-next-line */ }, [filtros]);

  const handleLimpar = async () => {
    if (!window.confirm("Confirma a limpeza dos logs conforme configuração?")) return;
    setLimpando(true);
    setPageError("");
    setPageSuccess("");
    try {
      const r = await api("POST", "/audit/limpar", configLimpar);
      setPageSuccess(r.message || "Limpeza concluída.");
      setShowLimpar(false);
      carregar(1);
      carregarStats();
    } catch (e) { setPageError(`Falha na limpeza: ${e.message}`); }
    setLimpando(false);
  };

  const carregarDetalhe = async (id) => {
    try {
      const r = await api("GET", `/audit/${id}`);
      setDetalhe(r.data);
    } catch (e) {
      setPageError(`Não foi possível carregar o detalhe: ${e.message}`);
    }
  };

  // ── Exportar CSV: faz fetch direto para preservar Content-Disposition ──
  const exportarCsv = async () => {
    setExportando(true);
    setPageError("");
    try {
      const qs = new URLSearchParams({
        ...Object.fromEntries(Object.entries(filtros).filter(([, v]) => v)),
        formato: "csv",
      });
      const token = localStorage.getItem("accessToken");
      const url = `${getApiBaseUrl()}/audit/exportar?${qs}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      setPageSuccess("CSV exportado com sucesso.");
    } catch (e) { setPageError(`Falha ao exportar CSV: ${e.message}`); }
    setExportando(false);
  };

  // ── Exportar PDF via window.print() de uma janela auxiliar ──
  const exportarPdf = async () => {
    setExportando(true);
    setPageError("");
    try {
      const qs = new URLSearchParams({
        ...Object.fromEntries(Object.entries(filtros).filter(([, v]) => v)),
        formato: "json",
      });
      const r = await api("GET", `/audit/exportar?${qs}`);
      const dados = r.data || [];
      const win = window.open("", "_blank", "width=1000,height=700");
      if (!win) {
        setPageError("Não foi possível abrir a janela de impressão. Permita pop-ups no navegador.");
        setExportando(false); return;
      }
      const rows = dados.map(l => {
        const badge = ACAO_BADGE[l.acao] || { bg:"#F1F5F9", color:"var(--gray-500)", label:l.acao };
        return `
          <tr>
            <td style="font-family:monospace;font-size:10px;white-space:nowrap">${fmtDT(l.createdAt)}</td>
            <td>${(l.nmUsuario || "—").replace(/</g, "&lt;")}
              <div style="font-size:9px;color:#94A3B8">${(l.perfil || "").replace(/</g, "&lt;")}</div></td>
            <td>${l.modulo || ""}</td>
            <td><span style="background:${badge.bg};color:${badge.color};padding:2px 6px;border-radius:8px;font-weight:700;font-size:10px">${badge.label}</span></td>
            <td>${(l.entidade ? `[${l.entidade}#${l.idEntidade}] ` : "") + (l.descricao || "").replace(/</g, "&lt;")}</td>
            <td style="font-family:monospace;font-size:10px;color:#94A3B8">${l.ip || "—"}</td>
          </tr>`;
      }).join("");
      win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
        <title>Logs de Auditoria — ${new Date().toLocaleDateString("pt-BR")}</title>
        <style>
          body{font-family:Arial,sans-serif;padding:20px;color:#1E293B}
          h1{font-size:18px;margin:0 0 4px}
          .meta{font-size:11px;color:#64748B;margin-bottom:16px}
          table{width:100%;border-collapse:collapse;font-size:11px}
          th{background:#F1F5F9;color:#475569;text-align:left;padding:6px;border-bottom:2px solid #CBD5E1}
          td{padding:5px 6px;border-bottom:1px solid #E2E8F0;vertical-align:top}
          @media print { @page { margin:14mm; size:landscape; } }
        </style>
        </head><body onload="window.print()">
        <h1>📋 Logs de Auditoria — Horizon eCondo</h1>
        <div class="meta">Gerado em ${new Date().toLocaleString("pt-BR")} · ${dados.length} registro(s)</div>
        <table>
          <thead><tr><th>Data/Hora</th><th>Usuário</th><th>Módulo</th><th>Ação</th><th>Descrição</th><th>IP</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
        </body></html>`);
      win.document.close();
      setPageSuccess("PDF aberto na janela de impressão.");
    } catch (e) { setPageError(`Falha ao exportar PDF: ${e.message}`); }
    setExportando(false);
  };

  const totalPages = Math.ceil(total / LIMIT);
  const set = (f, v) => setFiltros(p => ({ ...p, [f]: v }));

  const limparFiltros = () => {
    setFiltros({ idCondominio:"", modulo:"", acao:"", dtIni:"", dtFim:"", busca:"" });
  };

  const filtrosAtivos = useMemo(() =>
    Object.values(filtros).filter(Boolean).length,
  [filtros]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title"><i className="bi bi-shield-check me-2" style={{ color:"var(--primary)" }}/>Logs de Auditoria</h1>
          <p className="page-subtitle">Histórico completo de ações realizadas no sistema</p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-primary btn-sm" onClick={exportarCsv} disabled={exportando || !total}
            title="Exportar registros filtrados em CSV (Excel)">
            <i className="bi bi-filetype-csv me-1"/>CSV
          </button>
          <button className="btn btn-outline-primary btn-sm" onClick={exportarPdf} disabled={exportando || !total}
            title="Exportar registros filtrados em PDF">
            <i className="bi bi-filetype-pdf me-1"/>PDF
          </button>
          <button className="btn btn-outline-danger btn-sm" onClick={() => setShowLimpar(true)}>
            <i className="bi bi-trash3 me-1"/>Limpar Logs Antigos
          </button>
        </div>
      </div>

      <div style={{ padding:16 }}>
        <Banner tipo="error"   onClose={() => setPageError("")}>{pageError}</Banner>
        <Banner tipo="success" onClose={() => setPageSuccess("")}>{pageSuccess}</Banner>

        {/* Stats cards */}
        {stats && (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12,marginBottom:20}}>
            {[
              { label:"Ações hoje",     val:stats.hoje,   icon:"bi-calendar-check", color:"#2563EB", bg:"#DBEAFE" },
              { label:"Últimos 7 dias", val:stats.semana, icon:"bi-calendar-week",  color:"#7C3AED", bg:"#F3E8FF" },
              { label:"Total filtrado", val:total,         icon:"bi-list-check",     color:"#059669", bg:"#D1FAE5" },
            ].map((c, i) => (
              <div key={i} style={{
                background:"var(--bg-surface)", borderRadius:10, padding:"14px 18px",
                boxShadow:"0 1px 4px rgba(0,0,0,.08)", borderLeft:`4px solid ${c.color}`,
                display:"flex", justifyContent:"space-between", alignItems:"center",
              }}>
                <div>
                  <div style={{ fontSize:22, fontWeight:800, color:"var(--text-primary)" }}>{(c.val ?? 0).toLocaleString("pt-BR")}</div>
                  <div style={{ fontSize:11, color:"var(--gray-500)", fontWeight:600, textTransform:"uppercase" }}>{c.label}</div>
                </div>
                <div style={{ width:40, height:40, borderRadius:10, background:c.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <i className={`bi ${c.icon}`} style={{ fontSize:18, color:c.color }}/>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Filtros */}
        <div className="form-section" style={{ marginBottom:16 }}>
          <div className="row g-2 align-items-end">
            <div className="col-md-2">
              <label className="form-label">Condomínio</label>
              <select className="form-select form-select-sm" value={filtros.idCondominio} onChange={e => set("idCondominio", e.target.value)}>
                <option value="">Todos</option>
                {condominios.map(c => <option key={c.id} value={c.id}>{c.nmFantasia || c.nmCondominio}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Módulo</label>
              <select className="form-select form-select-sm" value={filtros.modulo} onChange={e => set("modulo", e.target.value)}>
                <option value="">Todos</option>
                {modulosOpts.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Ação</label>
              <select className="form-select form-select-sm" value={filtros.acao} onChange={e => set("acao", e.target.value)}>
                <option value="">Todas</option>
                {Object.entries(ACAO_BADGE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Data Início</label>
              <input type="date" className="form-control form-control-sm" value={filtros.dtIni} onChange={e => set("dtIni", e.target.value)}/>
            </div>
            <div className="col-md-2">
              <label className="form-label">Data Fim</label>
              <input type="date" className="form-control form-control-sm" value={filtros.dtFim} onChange={e => set("dtFim", e.target.value)}/>
            </div>
            <div className="col-md-2">
              <label className="form-label">Busca</label>
              <input className="form-control form-control-sm" placeholder="usuário, descrição..." value={filtros.busca} onChange={e => set("busca", e.target.value)}/>
            </div>
          </div>
          {filtrosAtivos > 0 && (
            <div style={{ marginTop:8, fontSize:12, color:"var(--gray-500)" }}>
              <i className="bi bi-funnel-fill me-1"/>{filtrosAtivos} filtro(s) ativo(s)
              <button className="btn btn-link btn-sm" style={{ fontSize:12, padding:"0 6px" }} onClick={limparFiltros}>
                Limpar filtros
              </button>
            </div>
          )}
        </div>

        {/* Tabela */}
        <div style={{ background:"var(--bg-surface)", borderRadius:10, boxShadow:"0 1px 4px rgba(0,0,0,.08)" }}>
          <div style={{ padding:"12px 16px", borderBottom:"1px solid var(--border-subtle)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={{ fontSize:13, fontWeight:600, color:"var(--gray-600)" }}>
              {total.toLocaleString("pt-BR")} registro(s)
            </span>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => carregar(page)}>
              <i className="bi bi-arrow-repeat me-1"/>Atualizar
            </button>
          </div>

          {loading ? (
            <div style={{ textAlign:"center", padding:40 }}><span className="spinner-border text-primary"/></div>
          ) : logs.length === 0 ? (
            <div style={{ textAlign:"center", padding:40, color:"var(--gray-400)" }}>
              <i className="bi bi-shield-check" style={{ fontSize:40, display:"block", marginBottom:8 }}/>
              Nenhum log encontrado{filtrosAtivos > 0 ? " com os filtros aplicados." : "."}
            </div>
          ) : (
            <table className="table table-custom" style={{ fontSize:12, marginBottom:0 }}>
              <thead>
                <tr>
                  <th style={{ width:140 }}>Data/Hora</th>
                  <th>Usuário</th>
                  <th>Módulo</th>
                  <th>Ação</th>
                  <th>Descrição</th>
                  <th style={{ width:80 }}>IP</th>
                  <th style={{ width:50 }}></th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => {
                  const badge       = ACAO_BADGE[log.acao]    || { bg:"#F1F5F9", color:"var(--gray-500)", label:log.acao };
                  const perfilBadge = PERFIL_BADGE[log.perfil] || { bg:"#F1F5F9", color:"var(--gray-500)" };
                  const critica     = ACOES_CRITICAS.has(log.acao);
                  return (
                    <tr key={log.id} style={critica ? { background:"#FEF7F7" } : undefined}>
                      <td style={{ fontFamily:"monospace", fontSize:11, whiteSpace:"nowrap" }}>
                        {fmtDT(log.createdAt)}
                      </td>
                      <td>
                        <div style={{ fontWeight:600 }}>{log.nmUsuario || "—"}</div>
                        {log.perfil && (
                          <span style={{
                            fontSize:10, padding:"1px 6px", borderRadius:8,
                            background:perfilBadge.bg, color:perfilBadge.color, fontWeight:700,
                          }}>{log.perfil}</span>
                        )}
                      </td>
                      <td>
                        <span style={{
                          fontSize:11, padding:"2px 8px", borderRadius:10,
                          background:"var(--bg-page)", color:"var(--gray-600)", fontWeight:600,
                        }}>
                          {log.modulo}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          fontSize:11, padding:"2px 8px", borderRadius:10,
                          background:badge.bg, color:badge.color, fontWeight:700,
                        }}>
                          {critica && <i className="bi bi-exclamation-triangle-fill me-1" style={{ fontSize:10 }}/>}
                          {badge.label}
                        </span>
                      </td>
                      <td style={{ maxWidth:300, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {log.entidade && <span style={{ color:"var(--gray-400)", marginRight:6 }}>[{log.entidade} #{log.idEntidade}]</span>}
                        {log.descricao}
                      </td>
                      <td style={{ fontSize:10, color:"var(--gray-400)", fontFamily:"monospace" }}>
                        {log.ip || "—"}
                      </td>
                      <td>
                        <button className="btn btn-outline-secondary btn-sm btn-icon"
                          title="Ver detalhe" onClick={() => carregarDetalhe(log.id)}>
                          <i className="bi bi-eye"/>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div style={{ padding:"12px 16px", borderTop:"1px solid #F1F5F9", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontSize:12, color:"var(--gray-500)" }}>Página {page} de {totalPages}</span>
              <div className="d-flex gap-1">
                <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => carregar(page - 1)}>
                  <i className="bi bi-chevron-left"/>
                </button>
                {Array.from({ length:Math.min(5, totalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                  return p <= totalPages ? (
                    <button key={p} className={`btn btn-sm ${p === page ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => carregar(p)}>{p}</button>
                  ) : null;
                })}
                <button className="btn btn-outline-secondary btn-sm" disabled={page >= totalPages} onClick={() => carregar(page + 1)}>
                  <i className="bi bi-chevron-right"/>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal detalhe */}
      {detalhe && (
        <div className="modal fade show d-block" style={{ background:"rgba(0,0,0,.5)" }} onClick={() => setDetalhe(null)}>
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-shield-check me-2"/>Detalhe do Log #{detalhe.id}
                </h5>
                <button className="btn-close" onClick={() => setDetalhe(null)}/>
              </div>
              <div className="modal-body" style={{ maxHeight:"75vh", overflowY:"auto" }}>
                <div className="row g-3">
                  {[
                    { label:"Data/Hora", val:fmtDT(detalhe.createdAt) },
                    { label:"Usuário",   val:`${detalhe.nmUsuario || "—"} (${detalhe.perfil || "—"})` },
                    { label:"Módulo",    val:detalhe.modulo },
                    { label:"Ação",      val:(ACAO_BADGE[detalhe.acao]?.label || detalhe.acao) },
                    { label:"Entidade",  val:`${detalhe.entidade || "—"} #${detalhe.idEntidade || "—"}` },
                    { label:"IP",        val:detalhe.ip || "—" },
                  ].map((f, i) => (
                    <div key={i} className="col-md-6">
                      <label className="form-label text-muted" style={{ fontSize:11 }}>{f.label}</label>
                      <div style={{ fontWeight:600, fontSize:13 }}>{f.val}</div>
                    </div>
                  ))}
                  {detalhe.userAgent && (
                    <div className="col-12">
                      <label className="form-label text-muted" style={{ fontSize:11 }}>Navegador</label>
                      <div style={{ fontSize:11, color:"var(--gray-500)", fontFamily:"monospace" }}>{detalhe.userAgent}</div>
                    </div>
                  )}
                  <div className="col-12">
                    <label className="form-label text-muted" style={{ fontSize:11 }}>Descrição</label>
                    <div style={{ fontWeight:600, fontSize:13 }}>{detalhe.descricao || "—"}</div>
                  </div>
                  {detalhe.dadosAntes && (
                    <div className="col-md-6">
                      <label className="form-label text-muted" style={{ fontSize:11 }}>Dados Antes</label>
                      <pre style={{ fontSize:11, background:"var(--bg-danger-soft)", borderRadius:6, padding:8, maxHeight:200, overflow:"auto" }}>
                        {JSON.stringify(typeof detalhe.dadosAntes === "string" ? JSON.parse(detalhe.dadosAntes) : detalhe.dadosAntes, null, 2)}
                      </pre>
                    </div>
                  )}
                  {detalhe.dadosDepois && (
                    <div className="col-md-6">
                      <label className="form-label text-muted" style={{ fontSize:11 }}>Dados Depois</label>
                      <pre style={{ fontSize:11, background:"var(--bg-success-soft)", borderRadius:6, padding:8, maxHeight:200, overflow:"auto" }}>
                        {JSON.stringify(typeof detalhe.dadosDepois === "string" ? JSON.parse(detalhe.dadosDepois) : detalhe.dadosDepois, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Limpeza */}
      {showLimpar && (
        <div className="modal fade show d-block" style={{ background:"rgba(0,0,0,.5)" }} onClick={() => setShowLimpar(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header" style={{ background:"var(--bg-danger-soft)" }}>
                <h5 className="modal-title text-danger">
                  <i className="bi bi-trash3 me-2"/>Limpar Logs Antigos
                </h5>
                <button className="btn-close" onClick={() => setShowLimpar(false)}/>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning py-2 mb-3" style={{ fontSize:13 }}>
                  <i className="bi bi-exclamation-triangle me-2"/>
                  Esta ação é <strong>irreversível</strong>. Logs removidos não poderão ser recuperados.
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Remover logs <strong>READ/LOGIN</strong> mais antigos que:
                  </label>
                  <div className="input-group">
                    <input type="number" className="form-control" value={configLimpar.dias_read}
                      onChange={e => setConfigLimpar(p => ({ ...p, dias_read:e.target.value }))}/>
                    <span className="input-group-text">dias</span>
                  </div>
                  <small className="text-muted">Padrão: 90 dias</small>
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Remover logs <strong>gerais</strong> (CREATE/UPDATE) mais antigos que:
                  </label>
                  <div className="input-group">
                    <input type="number" className="form-control" value={configLimpar.dias_geral}
                      onChange={e => setConfigLimpar(p => ({ ...p, dias_geral:e.target.value }))}/>
                    <span className="input-group-text">dias</span>
                  </div>
                  <small className="text-muted">Padrão: 365 dias (1 ano)</small>
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Remover <strong>todos os logs</strong> mais antigos que:
                  </label>
                  <div className="input-group">
                    <input type="number" className="form-control" value={configLimpar.dias_financeiro}
                      onChange={e => setConfigLimpar(p => ({ ...p, dias_financeiro:e.target.value }))}/>
                    <span className="input-group-text">dias</span>
                  </div>
                  <small className="text-muted">Padrão: 1825 dias (5 anos) — obrigação legal</small>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowLimpar(false)}>Cancelar</button>
                <button className="btn btn-danger" onClick={handleLimpar} disabled={limpando}>
                  {limpando
                    ? <><span className="spinner-border spinner-border-sm me-2"/>Limpando...</>
                    : <><i className="bi bi-trash3 me-2"/>Confirmar Limpeza</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
